import React, { useEffect, useState } from 'react'
import './Bot.scss'
import { botId } from '../../../../utils/embeddingUtils'
import axios from 'axios'
import { useAuth } from '../../../modules/auth'
const APP_API_URL = process.env.REACT_APP_API_URL
const API_URL = process.env.REACT_APP_PYTHON_API_URL

export const Bot: React.FC = () => {
  const [isMinimized, setIsMinimized] = useState(false)
  const [userInput, setUserInput] = useState('')
  const [chatMessages, setChatMessages] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const { currentUser } = useAuth()
  const userId = currentUser?.id
  const tenantId = currentUser?.tenantId

  const getBotIDFromURL = () => {
    const urlParts = window.location.href.split('/')
    const botID = urlParts[urlParts.length - 1]
    return botID
  }

  const botID = getBotIDFromURL()

  const toggleChatbox = () => {
    setIsMinimized(!isMinimized)
  }

  const saveBotTokens = async () => {
    try {
      const response = await axios.post(`${APP_API_URL}/save_token_count/${userId}/${botID}`, {
        tenantId,
      })
      const data = await response.data
    } catch (error) {
      console.error('Error fetching chat data:', error)
    }
  }

  useEffect(() => {
    saveBotTokens()
  }, [botID])

  // Auto-scroll to bottom when new messages are added
  useEffect(() => {
    const chatbox = document.getElementById('chatbox')
    if (chatbox) {
      chatbox.scrollTop = chatbox.scrollHeight
    }
  }, [chatMessages])

  const saveChatData = async () => {
    try {
      const response = await fetch(`${APP_API_URL}/saveChatData/${botId}`)
      const data = await response.json()
    } catch (error) {
      console.error('Error fetching chat data:', error)
    }
  }

  // Fixed message functions to use functional state updates
  const addUserMessage = (message: string) => {
    setChatMessages(prevMessages => [...prevMessages, `User: ${message}`])
  }

  const addBotMessage = (message: string) => {
    setChatMessages(prevMessages => [...prevMessages, `Bot: ${message}`])
  }

  const respondToUser = async (userMessage: string) => {
    setIsLoading(true) // Start loading
    try {
      const response = await fetch(`${API_URL}/ask_question/${botID}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ user_question: userMessage }),
      })

      if (!response.ok) {
        throw new Error('Chatbot request failed')
      }

      const data = await response.json()
      console.log('API Response:', data) // Debug log to see the response structure

      saveBotTokens()
      saveChatData()
      const subscriptionData = await axios.get(`${APP_API_URL}/fetchSubsDetails/${userId}`)
      const maxTokenLimit = +subscriptionData.data.plntokens
      const totalTokenUsed = await axios.get(`${APP_API_URL}/get_token_count/${userId}/${botID}`)

      if (maxTokenLimit < totalTokenUsed.data.totalTokens) {
        addBotMessage('Token limit exceeded.')
        setIsLoading(false) // Stop loading
        return
      }

      // FIXED: Only add the current response, not all previous messages
      // Check if there's a direct answer field first
      if (data.answer) {
        addBotMessage(data.answer)
      } else if (data.response) {
        addBotMessage(data.response)
      } else if (data.reply) {
        addBotMessage(data.reply)
      } else {
        // If the API returns messages array, only take the latest bot response
        const botMessages = data.messages || []
        if (botMessages.length > 0) {
          // Get the last message (which should be the current response)
          const latestMessage = botMessages[botMessages.length - 1]
          let messageContent = ''

          if (typeof latestMessage.content === 'object' && latestMessage.content !== null) {
            messageContent = latestMessage.content.content || latestMessage.content.answer || JSON.stringify(latestMessage.content)
          } else if (typeof latestMessage === 'object' && latestMessage.content) {
            messageContent = latestMessage.content
          } else {
            messageContent = latestMessage.content || latestMessage
          }

          addBotMessage(messageContent)
        }
      }
    } catch (error) {
      console.error('Error communicating with the chatbot:', error)
      addBotMessage('Sorry, there was an error processing your request.')
    } finally {
      setIsLoading(false) // Stop loading regardless of success or error
    }
  }

  const handleSendButtonClick = () => {
    if (userInput.trim() !== '' && !isLoading) { // Prevent sending while loading
      addUserMessage(userInput)
      respondToUser(userInput)
      setUserInput('')
    }
  }

  const handleUserInputKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && !isLoading) { // Prevent sending while loading
      handleSendButtonClick()
    }
  }

  return (
    <div className='d-flex flex-column overflow-auto'>
      <div className='try-bot-header'>
        <div className='try-bot-inner'>
          <h2 className='chakra-heading css-bdf3ph'>Try out chatbot</h2>
          <p className='chakra-text css-q9k0mw'>
            Chat with your chatbot and see how it responds. If you don't get the desired response,
            follow the instructions below.
          </p>
        </div>
      </div>
      <div className='d-flex flex-row '>
        <div className='d-flex flex-column w-50 p-7 '>
          <div
            className='alert alert-primary d-flex flex-row align-items-center rounded'
            role='alert'
          >
            <span>
              <i className='bi bi-exclamation-diamond me-3'></i>
            </span>

            <div className='alert-msg'>
              <h3>Not getting proper response</h3>
              <p className='text-primary'>
                It is all about the training data. Here are some ways to improve the accuracy of
                your chatbot.
              </p>
            </div>
          </div>
          <div className='tips-1'>
            <div className='tip-number'> 1</div>
            <h2 className='chakra-heading EditChatbot_resultTipHeading__L7BYQ css-1owbjjc'>
              Ensure Correct Crawling of data from pdf.
            </h2>
            <p>
              The more data you add, the better the chatbot response will be. You can add custom
              data to train in the form of question-answer pairs from the provided data.
            </p>
          </div>
          <div className='tips-1'>
            <div className='tip-number'>2</div>
            <h2>Train Chatbot with Previous Support Questions.</h2>
            <p>
              To reduce the number of repetitive emails and inquiries, make sure to train your
              chatbot with past support questions and answers.
              <span className='chakra-text css-1lglquy'>Train custom data</span> section{' '}
            </p>
          </div>
          <div className='tips-1'>
            <div className='tip-number'> 3</div>
            <h2>Contact Us</h2>
            <p>
              Still not satisfied? Email us at
              <a href='mailto:info@aapnainfotech.com'> AapnaInfotech</a>. We'll help you with the
              setup.
            </p>
          </div>
        </div>
        <div className='w-50 align-items-end' style={{ position: 'relative', zIndex: 1000 }}>
          <div
            id='chat-container'
            className={` ${isMinimized ? 'minimized' : ''}`}
            style={{ position: 'relative', zIndex: 1001 }}
          >
            <div className='shadow rounded-lg max-w-lg w-full inner-chat-container'>
              <div className='p-4 border-bottom bg-primary text-white rounded-top d-flex justify-content-between align-items-center'>
                <p className='text-lg font-weight-bold'>I am your AI assistant. Ask me anything</p>
                <button id='close-chat' className='btn btn-link text-light' onClick={toggleChatbox}>
                  <i className='bi bi-x'></i>
                </button>
              </div>
              <div
                id='chatbox'
                className='p-4 overflow-auto card-body'
                style={{
                  height: '300px',
                  backgroundColor: 'white',
                  position: 'relative',
                  zIndex: 1002,
                  overflowY: 'auto',
                  overflowX: 'hidden'
                }}
              >
                {chatMessages.map((message, index) => {
                  const isUserMessage = message.startsWith('User:')
                  return (
                    <div
                      key={index}
                      className={`mb-2 d-flex ${isUserMessage ? 'justify-content-end' : 'justify-content-start'}`}
                      style={{ position: 'relative', zIndex: 1003 }}
                    >
                      <div
                        className={`rounded py-2 px-3 inline-block ${isUserMessage
                            ? 'bg-primary text-white'
                            : 'bg-light text-dark border'
                          }`}
                        style={{
                          maxWidth: '80%',
                          wordWrap: 'break-word',
                          overflowWrap: 'break-word',
                          whiteSpace: 'pre-wrap'
                        }}
                      >
                        {message.replace(/^(User:|Bot:)\s*/, '')}
                      </div>
                    </div>
                  )
                })}

                {/* Loading indicator */}
                {isLoading && (
                  <div className="mb-2 d-flex justify-content-start" style={{ position: 'relative', zIndex: 1003 }}>
                    <div
                      className="rounded py-2 px-3 inline-block bg-light text-dark border"
                      style={{
                        maxWidth: '80%',
                        wordWrap: 'break-word',
                        overflowWrap: 'break-word',
                        whiteSpace: 'pre-wrap'
                      }}
                    >
                      <div className="typing-dots">
                        <span>.</span>
                        <span>.</span>
                        <span>.</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div
                className='p-4 border-top d-flex'
                style={{ backgroundColor: 'white', position: 'relative', zIndex: 1002 }}
              >
                <input
                  id='user-input'
                  type='text'
                  placeholder={isLoading ? 'Waiting for response...' : 'Type a message'}
                  className='form-control rounded-start'
                  value={userInput}
                  onChange={(e) => setUserInput(e.target.value)}
                  onKeyPress={handleUserInputKeyPress}
                  disabled={isLoading}
                />
                <button
                  id='send-button'
                  className='btn btn-primary rounded-end'
                  onClick={handleSendButtonClick}
                  disabled={isLoading}
                >
                  {isLoading ? '...' : 'Send'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}